import React, { useImperativeHandle, useRef, useState } from "react";
import clsx from "clsx";
import { Card, RadioField, TextField, Typography, Box } from "@curves/ui-base";
import { formatCurrencyFn, isEqual, refIsRequiredError } from "@util/utils";
import useForkRef from "@util/hook/useForkRef";

/**
 * 📦 MultiplePaymentAmountPaidListGroup
 * 用於顯示「現金」與「信用卡」兩種支付欄位，以及總金額（合計）欄位。
 * 可支援退款模式、唯讀模式，並透過 ref 提供資料取得與錯誤檢查方法。
 */
export const MultiplePaymentAmountPaidListGroup = React.forwardRef((props, ref) => {
	const { className, cashPaymentAmountProps = {}, cardPaymentAmountProps = {}, total, readOnly, isRefund = false } = props;
	const { inputProps: cashInputProps = { placeholder: "請輸入金額", min: 0, required: true }, ...cashOther } = cashPaymentAmountProps;
	const { inputProps: cardInputProps = { placeholder: "請輸入金額", min: 0, required: true }, ...cardOther } = cardPaymentAmountProps;
	const cashPaymentAmountRef = useRef(null);
	const cardPaymentAmountRef = useRef(null);

	useImperativeHandle(
		ref,
		() => ({
			getResult: () => {
				return {
					cashPaymentAmount: cashPaymentAmountRef.current.getResult(),
					cardPaymentAmount: cardPaymentAmountRef.current.getResult(),
				};
			},
			isError: () => refIsRequiredError(cashPaymentAmountRef, cardPaymentAmountRef),
		}),
		// eslint-disable-next-line
		[]
	);

	return (
		<PaidListGroup className={className}>
			<PaidListItem label="合計" className="font-weight-bold">
				<strong className="paid-list-cell text-primary">{formatCurrencyFn(total)}</strong>
			</PaidListItem>
			<PaidListItem label={isRefund ? "現金還款金額" : "現金收款金額"}>
				<TextField maskType="MONEY" ref={cashPaymentAmountRef} inputProps={cashInputProps} readOnly={readOnly} {...cashOther} />
			</PaidListItem>
			<PaidListItem label={isRefund ? "信用卡還款金額" : "信用卡收款金額"}>
				<TextField maskType="MONEY" ref={cardPaymentAmountRef} inputProps={cardInputProps} readOnly={readOnly} {...cardOther} />
			</PaidListItem>
		</PaidListGroup>
	);
});

/**
 * 📦 PaymentAmountPaidListGroup
 * 用於顯示單一支付方式（現金或信用卡），並可切換支付類型的簡化版欄位群組。
 * 支援合計金額顯示與付款類型切換（Radio），由 ref 暴露結果與錯誤檢查。
 */
export const PaymentAmountPaidListGroup = React.forwardRef((props, ref) => {
	const { className, cashPaymentAmountProps, cardPaymentAmountProps, total, readOnly } = props;
	const paymentAmountProps = cashPaymentAmountProps || cardPaymentAmountProps;
	const {
		inputProps: paymentAmountInputProps = {
			placeholder: "請輸入金額",
			min: 0,
			required: true,
		},
		...other
	} = paymentAmountProps;
	const [paymentType, setPaymentType] = useState("cash");
	const paymentAmountRef = useRef(null);

	const handleChangePaymentType = e => {
		setPaymentType(e.target.value);
	};

	const handleCurrentPaymentType = paymentType => {
		let current;
		if (isEqual(paymentType, "cash")) {
			current = "cashPaymentAmount";
		} else if (isEqual(paymentType, "card")) {
			current = "cardPaymentAmount";
		}
		return current;
	};

	useImperativeHandle(ref, () => ({
		getResult: () => {
			let current = handleCurrentPaymentType(paymentType);
			return {
				[current]: paymentAmountRef.current.getResult(),
			};
		},
		isError: () => refIsRequiredError(paymentAmountRef),
	}));
	return (
		<PaidListGroup className={className}>
			<PaidListItem label="合計">
				<Box component="strong" className="paid-list-cell text-primary">
					{formatCurrencyFn(total)}
				</Box>
			</PaidListItem>
			<PaidListItem label="收款方式">
				<RadioField value={paymentType} onChange={handleChangePaymentType} readOnly={readOnly} required row>
					<RadioField.Radio name="paymentType" value="cash" label="現金" />
					<RadioField.Radio name="paymentType" value="card" label="信用卡" />
				</RadioField>
			</PaidListItem>
			<PaidListItem label="收款金額">
				<TextField maskType="MONEY" ref={paymentAmountRef} inputProps={paymentAmountInputProps} readOnly={readOnly} {...other} />
			</PaidListItem>
		</PaidListGroup>
	);
});

/**
 * 📦 PaidListTotalGroup
 * 費用合計區塊，可搭配 children 傳入額外元件。
 * 用於顯示單純合計金額與補充欄位的排版需求。
 */
export const PaidListTotalGroup = props => {
	const { className, total, children } = props;
	return (
		<PaidListGroup className={className}>
			<PaidListItem label="合計" className="font-weight-bold">
				<Box component="strong" className="paid-list-cell text-primary">
					{formatCurrencyFn(total)}
				</Box>
			</PaidListItem>
			{children}
		</PaidListGroup>
	);
};

/**
 * 📦 PaidListFeeSummaryGroup
 * 顯示「金額差額結果」的提示區塊。
 * 當金額不符（如未收清或超收）時顯示警示文字與樣式，通常搭配結帳畫面使用。
 */
export const PaidListFeeSummaryGroup = props => {
	const { className, balance } = props;
	let label = "未收金額";
	if (balance < 0) {
		label = "收款金額已超過應收金額，無法進行結帳。";
	}
	return (
		<PaidListGroup className={className}>
			<PaidListItem label={label} className="balance">
				<Box component="strong" className="paid-list-cell text-error">
					{formatCurrencyFn(balance)}
				</Box>
			</PaidListItem>
		</PaidListGroup>
	);
};

/**
 * 🧩 PaidListItem
 * 個別收費欄位元件，用於顯示標籤與輸入欄位。
 */
const PaidListItem = props => {
	const { label, children, className, required } = props;
	const hasChildren = React.Children.count(children) > 0;

	return (
		<React.Fragment>
			<Box component="li" {...props} className={clsx("paid-list-item", className)}>
				{label && (
					<Box component="span" className="paid-list-cell">
						{label}
						{required && (
							<Box component="span" className="required">
								*
								<Box component="span" className="text">
									必填
								</Box>
							</Box>
						)}
					</Box>
				)}
				{hasChildren && React.cloneElement(children, { required: children.props.required || required })}
			</Box>
		</React.Fragment>
	);
};

/**
 * 🧩 PaidListGroup
 * 收費欄位群組容器，用 ul 排版支援 PaidListItem 排列。
 */
const PaidListGroup = props => {
	const { className, children } = props;

	return (
		<Box component="ul" className={clsx("paid-list", className)}>
			{children}
		</Box>
	);
};

/**
 * 🧾 PaidList
 * 主容器元件，整合各種付款欄位呈現方式（單一 / 多重），
 * 同時支援金額合計與差額提示（FeeSummary）。
 * 通常作為常用付款欄位。
 */
const PaidList = React.forwardRef((props, ref) => {
	const { className, title, children, isMultiplePayment = true, cashPaymentAmountProps, cardPaymentAmountProps, total, isShowBalance = false, balance, readOnly = false, ...other } = props;
	const paymentAmountRef = useRef();
	const combinedRef = useForkRef(ref, paymentAmountRef);

	return (
		<Card {...other} className={clsx("paid-list-card", className)}>
			<Card.CardContent className="px-4 py-3">
				{title && (
					<Typography className="mb-2 mt-0 font-weight-bold" variant="h5" component="h6">
						{title}
					</Typography>
				)}
				<Box component="ul" className="paid-list pt-0">
					{children}
				</Box>
				<Box component="ul" className="paid-list">
					{isMultiplePayment ? (
						<MultiplePaymentAmountPaidListGroup
							ref={combinedRef}
							total={total}
							cashPaymentAmountProps={cashPaymentAmountProps}
							cardPaymentAmountProps={cardPaymentAmountProps}
							readOnly={readOnly}
						/>
					) : (
						<PaymentAmountPaidListGroup
							ref={combinedRef}
							total={total}
							cashPaymentAmountProps={cashPaymentAmountProps}
							cardPaymentAmountProps={cardPaymentAmountProps}
							readOnly={readOnly}
						/>
					)}
				</Box>
				{isShowBalance && <PaidListFeeSummaryGroup balance={balance} />}
			</Card.CardContent>
		</Card>
	);
});

/**
 * 💡 PaidListLayout
 * 簡化版排版容器，提供 Card 外框與標題，可自由嵌入欄位組合。
 */
export const PaidListLayout = props => {
	const { className, title, children, other } = props;
	return (
		<Card {...other} className={clsx("paid-list-card", className)}>
			<Card.CardContent className="px-4 py-3">
				{title && (
					<Typography className="mb-0 mt-0 font-weight-bold" variant="h5" component="h6">
						{title}
					</Typography>
				)}
				{children}
			</Card.CardContent>
		</Card>
	);
};

PaidList.PaidListItem = PaidListItem;
PaidList.PaidListGroup = PaidListGroup;

export default PaidList;
